import { useNode, Element } from "@craftjs/core";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { applyStyleProps, defaultStyleProps, type ResponsiveStyleProps } from "@/lib/craft/style-props";
import { SectionBlockSettings } from "@/components/craft/settings/SectionBlockSettings";

type SectionBlockProps = {
  maxWidth?: "boxed" | "full";
  paddingY?: number;
  style?: ResponsiveStyleProps;
  children?: ReactNode;
};

export function SectionBlock({
  maxWidth = "boxed",
  paddingY = 48,
  style = defaultStyleProps(),
  children,
}: SectionBlockProps) {
  const {
    connectors: { connect, drag },
    selected,
    id,
  } = useNode((node) => ({ selected: node.events.selected, id: node.id }));

  const { style: inlineStyle, className: styleClass } = applyStyleProps(style, "desktop", id);

  return (
    <section
      ref={(ref) => {
        if (ref) connect(drag(ref));
      }}
      className={cn(
        "craft-node",
        `craft-node-${id}`,
        styleClass,
        selected && "outline outline-2 outline-dashed outline-primary/30",
      )}
      style={{
        ...inlineStyle,
        paddingTop: paddingY,
        paddingBottom: paddingY,
      }}
    >
      <div className={cn(maxWidth === "boxed" ? "container-page mx-auto" : "w-full")}>
        {children}
      </div>
    </section>
  );
}

SectionBlock.craft = {
  displayName: "Section",
  isCanvas: true,
  props: {
    maxWidth: "boxed",
    paddingY: 48,
    style: defaultStyleProps(),
  },
  rules: {
    canDrag: () => true,
    canMoveIn: (incoming) =>
      incoming.some((node) => node.data.name === "RowBlock"),
  },
  related: {
    settings: SectionBlockSettings,
  },
};

export function SectionCanvas({ children }: { children?: ReactNode }) {
  return (
    <Element is={SectionBlock} canvas maxWidth="boxed" paddingY={48}>
      {children}
    </Element>
  );
}
